import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Database, Globe, RefreshCw, Loader2, AlertTriangle,
  ToggleLeft, ToggleRight, Search, Filter, ExternalLink
} from 'lucide-react';

const COUNTRY_NAMES = {
  EU: 'EU Universal', PL: 'Poland', FI: 'Finland', SE: 'Sweden',
  NO: 'Norway', DK: 'Denmark', EE: 'Estonia', LV: 'Latvia',
  LT: 'Lithuania', DE: 'Germany', AT: 'Austria', BE: 'Belgium',
  BG: 'Bulgaria', HR: 'Croatia', CY: 'Cyprus', CZ: 'Czechia',
  FR: 'France', GR: 'Greece', HU: 'Hungary', IE: 'Ireland',
  IT: 'Italy', LU: 'Luxembourg', MT: 'Malta', NL: 'Netherlands',
  PT: 'Portugal', RO: 'Romania', SK: 'Slovakia', SI: 'Slovenia',
  ES: 'Spain', GB: 'United Kingdom', CH: 'Switzerland',
};

const CATEGORY_COLORS = {
  grants: 'bg-green-100 text-green-700',
  procurement: 'bg-blue-100 text-blue-700',
  permits: 'bg-orange-100 text-orange-700',
  eia: 'bg-yellow-100 text-yellow-700',
  trade_press: 'bg-purple-100 text-purple-700',
  enrichment: 'bg-gray-100 text-gray-700',
};

const SourceRegistryAdmin = ({ loadSourceRegistry, updateSourceRegistryEntry }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [countryFilter, setCountryFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await loadSourceRegistry();
      setSources(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadSourceRegistry]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggleEnabled = async (source) => {
    setToggling(source.id);
    try {
      const updated = await updateSourceRegistryEntry(source.id, { is_enabled: !source.is_enabled });
      setSources(prev => prev.map(s => s.id === source.id ? updated : s));
    } catch (err) {
      setError(err.message);
    } finally {
      setToggling(null);
    }
  };

  // Get unique countries and categories for filters
  const uniqueCountries = [...new Set(sources.map(s => s.country_code))].sort();
  const uniqueCategories = [...new Set(sources.map(s => s.category))].sort();

  // Apply filters
  const filteredSources = sources.filter(s => {
    if (countryFilter && s.country_code !== countryFilter) return false;
    if (categoryFilter && s.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return s.source_name.toLowerCase().includes(q) ||
        s.endpoint_url.toLowerCase().includes(q) ||
        (COUNTRY_NAMES[s.country_code] || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Group by country
  const grouped = {};
  filteredSources.forEach(s => {
    if (!grouped[s.country_code]) grouped[s.country_code] = [];
    grouped[s.country_code].push(s);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading source registry...</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-5 h-5 text-primary-600" />
            Country Source Registry
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {sources.length} sources · {sources.filter(s => s.is_enabled).length} enabled
          </p>
        </div>
        <button
          onClick={loadData}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search sources..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">All Countries</option>
          {uniqueCountries.map(cc => (
            <option key={cc} value={cc}>{COUNTRY_NAMES[cc] || cc} ({cc})</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">All Categories</option>
          {uniqueCategories.map(cat => (
            <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      {/* Source List by Country */}
      {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([country, countrySources]) => (
        <div key={country} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-900">
              {COUNTRY_NAMES[country] || country} ({country})
            </span>
            <span className="text-xs text-gray-500 ml-auto">
              {countrySources.filter(s => s.is_enabled).length}/{countrySources.length} enabled
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {countrySources.map(source => (
              <motion.div
                key={source.id}
                className={`px-4 py-3 flex items-center gap-4 transition-colors ${
                  !source.is_enabled ? 'bg-gray-50/50 opacity-70' : 'hover:bg-gray-50'
                }`}
              >
                {/* Toggle */}
                <button
                  onClick={() => handleToggleEnabled(source)}
                  disabled={toggling === source.id}
                  className="flex-shrink-0"
                >
                  {toggling === source.id ? (
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  ) : source.is_enabled ? (
                    <ToggleRight className="w-6 h-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-gray-400" />
                  )}
                </button>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-gray-900">{source.source_name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${CATEGORY_COLORS[source.category] || 'bg-gray-100 text-gray-600'}`}>
                      {source.category.replace('_', ' ')}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                      {source.source_type}
                    </span>
                    {source.is_eu_universal && (
                      <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">EU Universal</span>
                    )}
                    {source.requires_api_key && (
                      <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">API Key</span>
                    )}
                  </div>
                  <a
                    href={source.endpoint_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:text-primary-600 hover:underline flex items-center gap-1 truncate"
                  >
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    {source.endpoint_url}
                  </a>
                  {source.notes && (
                    <p className="text-xs text-gray-400 mt-0.5">{source.notes}</p>
                  )}
                </div>

                {/* Poll frequency */}
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {source.poll_frequency}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      ))}

      {filteredSources.length === 0 && (
        <div className="text-center py-8 bg-white border border-gray-200 rounded-xl">
          <Filter className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No sources match your filters.</p>
        </div>
      )}
    </div>
  );
};

export default SourceRegistryAdmin;
